/**
 * Concept Presentation Viewer — 9-page slide deck for the Robot-Assisted
 * Battery Cabinet Assembly section.
 *
 * COMPLIANCE:
 *   - Only uses the public PDF /downloads/NeuNexus_Robot_Assisted_Battery_Concept.pdf
 *   - If the file (or its per-page WebP previews) is missing, shows an
 *     admin-only warning explaining exactly which file must be uploaded.
 *   - Never renders "NeuNexus × NEURA" or any NEURA logo.
 *
 * Slide previews are expected at:
 *   /innovation/concept-presentation/page-01.webp … page-09.webp  (≤ 250 KB each)
 * Public PDF at:
 *   /downloads/NeuNexus_Robot_Assisted_Battery_Concept.pdf
 */
import React from "react";
import { ChevronLeft, ChevronRight, Maximize2, ExternalLink, Download, AlertTriangle, X } from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";

const P = {
  bg: "#080B12", bg2: "#0A0F1A", border: "rgba(255,255,255,0.08)",
  surface: "rgba(255,255,255,0.03)", ink: "#E8ECF0", dim: "rgba(232,236,240,0.68)",
  silver: "#C0C7CE", cyan: "#06B6D4", green: "#22C55E", amber: "#F59E0B", red: "#F43F5E",
};

const TOTAL_PAGES = 9;
const PDF_URL = "/downloads/NeuNexus_Robot_Assisted_Battery_Concept.pdf";
const PAGE_URL = (n) => `/innovation/concept-presentation/page-${String(n).padStart(2, "0")}.webp`;
const REQUIRED_FILE = "NeuNexus_Robot_Assisted_Battery_Concept.pdf";

const D = {
  en: {
    eyebrow: "Concept Presentation",
    title: "Explore the Concept Presentation",
    body: "View the proposed workflow, human control model, training approach and possible next steps for a future robot-assisted battery cabinet assembly pilot.",
    slideTitle: "NeuNexus — Robot-Assisted Battery Cabinet Assembly — Concept Presentation",
    prev: "Previous page", next: "Next page", fullscreen: "Full-screen", exitFs: "Exit full-screen",
    openPdf: "Open PDF", download: "Download Concept PDF",
    pageAlt: (n) => `Concept presentation page ${n} of ${TOTAL_PAGES}`,
    thumbAria: (n) => `Go to page ${n}`,
    disclaimer: "Concept presentation only. The proposed robot configuration, safety requirements, training process and implementation pathway have not been finalized. No confirmed technology partnership, technical approval or commercial deployment is implied.",
    warnTitle: "Admin notice — required public document not uploaded",
    warnBody: (f) => `The presentation viewer is prepared but cannot render the deck yet. Please upload the compliant public PDF named "${f}" to /app/frontend/public/downloads/ together with 9 optimized page previews at /app/frontend/public/innovation/concept-presentation/page-01.webp … page-09.webp (each ≤ 250 KB). Do not use any file containing "NeuNexus × NEURA", the NEURA logo, employee names, meeting information or partnership claims.`,
  },
  de: {
    eyebrow: "Konzeptpräsentation",
    title: "Konzeptpräsentation ansehen",
    body: "Sehen Sie den vorgeschlagenen Workflow, das Modell der menschlichen Kontrolle, den Trainingsansatz und mögliche nächste Schritte für einen zukünftigen roboterunterstützten Batterieschrank-Montagepilot.",
    slideTitle: "NeuNexus — Roboterunterstützte Batterieschrank-Montage — Konzeptpräsentation",
    prev: "Vorherige Seite", next: "Nächste Seite", fullscreen: "Vollbild", exitFs: "Vollbild verlassen",
    openPdf: "PDF öffnen", download: "Concept-PDF herunterladen",
    pageAlt: (n) => `Konzeptpräsentation Seite ${n} von ${TOTAL_PAGES}`,
    thumbAria: (n) => `Zu Seite ${n}`,
    disclaimer: "Nur Konzeptpräsentation. Die vorgeschlagene Roboterkonfiguration, Sicherheitsanforderungen, der Trainingsprozess und der Implementierungsweg sind nicht finalisiert. Keine bestätigte Technologiepartnerschaft, technische Freigabe oder kommerzielle Bereitstellung ist impliziert.",
    warnTitle: "Admin-Hinweis — benötigtes öffentliches Dokument nicht hochgeladen",
    warnBody: (f) => `Der Präsentations-Viewer ist bereit, kann das Deck aber noch nicht rendern. Bitte laden Sie die konforme öffentliche PDF-Datei "${f}" nach /app/frontend/public/downloads/ hoch, zusammen mit 9 optimierten Seitenvorschauen unter /app/frontend/public/innovation/concept-presentation/page-01.webp … page-09.webp (je ≤ 250 KB). Verwenden Sie keine Datei mit "NeuNexus × NEURA", NEURA-Logo, Mitarbeiternamen, Meeting-Informationen oder Partnerschaftsansprüchen.`,
  },
  pt: {
    eyebrow: "Apresentação de Conceito",
    title: "Explorar a Apresentação de Conceito",
    body: "Ver o fluxo proposto, o modelo de controlo humano, a abordagem de formação e os possíveis próximos passos para um futuro piloto de montagem de armários de bateria assistida por robot.",
    slideTitle: "NeuNexus — Montagem de Armários de Bateria Assistida por Robot — Apresentação de Conceito",
    prev: "Página anterior", next: "Página seguinte", fullscreen: "Ecrã inteiro", exitFs: "Sair do ecrã inteiro",
    openPdf: "Abrir PDF", download: "Descarregar PDF de Conceito",
    pageAlt: (n) => `Apresentação de conceito, página ${n} de ${TOTAL_PAGES}`,
    thumbAria: (n) => `Ir para a página ${n}`,
    disclaimer: "Apenas apresentação de conceito. A configuração de robot proposta, requisitos de segurança, processo de formação e percurso de implementação ainda não foram finalizados. Não é implicada qualquer parceria tecnológica confirmada, aprovação técnica ou implementação comercial.",
    warnTitle: "Aviso de administrador — documento público requerido não foi carregado",
    warnBody: (f) => `O visualizador da apresentação está preparado mas ainda não pode renderizar o deck. Por favor, carregue o PDF público conforme com o nome "${f}" em /app/frontend/public/downloads/ juntamente com 9 pré-visualizações de página optimizadas em /app/frontend/public/innovation/concept-presentation/page-01.webp … page-09.webp (cada uma ≤ 250 KB). Não utilize nenhum ficheiro que contenha "NeuNexus × NEURA", o logo NEURA, nomes de colaboradores, informações de reunião ou alegações de parceria.`,
  },
};

const useC = () => {
  const { lang } = React.useContext(LangCtx);
  return D[lang] || D.en;
};

export default function ConceptPresentationViewer() {
  const t = useC();
  const [page, setPage] = React.useState(1);
  const [available, setAvailable] = React.useState(null); // null = probing, true/false after HEAD
  const [fs, setFs] = React.useState(false);
  const touchX = React.useRef(null);
  const viewerRef = React.useRef(null);

  // Probe whether the required PDF exists (HEAD).
  React.useEffect(() => {
    let cancelled = false;
    fetch(PDF_URL, { method: "HEAD" })
      .then((r) => !cancelled && setAvailable(r.ok))
      .catch(() => !cancelled && setAvailable(false));
    return () => { cancelled = true; };
  }, []);

  // Keyboard navigation
  React.useEffect(() => {
    if (!available) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setPage((p) => Math.min(TOTAL_PAGES, p + 1));
      if (e.key === "ArrowLeft")  setPage((p) => Math.max(1, p - 1));
      if (e.key === "Escape" && fs) setFs(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [available, fs]);

  // Preload immediate neighbours (lazy loading elsewhere).
  React.useEffect(() => {
    if (!available) return;
    [page + 1, page - 1].filter((n) => n >= 1 && n <= TOTAL_PAGES).forEach((n) => {
      const img = new Image(); img.src = PAGE_URL(n);
    });
  }, [page, available]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) setPage((p) => Math.max(1, Math.min(TOTAL_PAGES, p + (dx < 0 ? 1 : -1))));
    touchX.current = null;
  };

  return (
    <div className="mt-10" data-testid="concept-presentation">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.cyan }}>{t.eyebrow}</div>
      <h3 className="mt-3 font-semibold tracking-tight text-[22px] lg:text-[28px]" style={{ color: "#fff", letterSpacing: "-0.015em" }}>
        {t.title}
      </h3>
      <p className="mt-3 max-w-3xl text-[13px] leading-relaxed" style={{ color: P.dim }}>{t.body}</p>

      {available === false && (
        <div className="mt-6 rounded-lg p-5 flex gap-4 items-start" data-testid="concept-admin-warning"
          style={{ background: `${P.amber}0d`, border: `1px solid ${P.amber}66`, color: P.ink }}>
          <AlertTriangle size={18} style={{ color: P.amber }} className="shrink-0 mt-0.5" />
          <div>
            <div className="text-[13px] font-semibold" style={{ color: "#fff" }}>{t.warnTitle}</div>
            <p className="mt-1.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>{t.warnBody(REQUIRED_FILE)}</p>
          </div>
        </div>
      )}

      {available && (
        <>
          {/* Public presentation title bar */}
          <div className="mt-6 rounded-t-xl px-4 py-3 flex items-center justify-between gap-3"
            style={{ background: P.bg2, border: `1px solid ${P.border}`, borderBottom: "none" }}>
            <div className="text-[11.5px] font-semibold truncate" style={{ color: P.ink }}>{t.slideTitle}</div>
            <div className="text-[11px] font-mono shrink-0" style={{ color: P.cyan }} data-testid="concept-page-indicator">
              {page} / {TOTAL_PAGES}
            </div>
          </div>

          {/* Slide */}
          <div ref={viewerRef}
            className={"relative " + (fs ? "fixed inset-0 z-[100] p-4 sm:p-8 flex items-center justify-center" : "")}
            style={fs ? { background: "rgba(4,6,10,0.96)", backdropFilter: "blur(6px)" } : {}}
            onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
            data-testid="concept-viewer">
            <div className={fs ? "relative w-full max-w-6xl" : "relative"}>
              <div className={"rounded-b-xl overflow-hidden " + (fs ? "rounded-xl" : "")}
                style={{ background: P.bg, border: `1px solid ${P.border}`, boxShadow: "0 20px 60px -20px rgba(0,0,0,0.55)" }}>
                <img
                  src={PAGE_URL(page)} alt={t.pageAlt(page)}
                  loading={page === 1 ? "eager" : "lazy"}
                  className="w-full h-auto block"
                  data-testid={`concept-page-${page}`}
                  style={{ aspectRatio: "16 / 9", objectFit: "contain", background: "#04070c" }}
                />
                {/* Prev / next arrows overlay */}
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  aria-label={t.prev} data-testid="concept-prev"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  style={{ background: "rgba(8,11,18,0.85)", border: `1px solid ${P.cyan}55`, color: P.cyan }}>
                  <ChevronLeft size={18} />
                </button>
                <button type="button" onClick={() => setPage((p) => Math.min(TOTAL_PAGES, p + 1))} disabled={page === TOTAL_PAGES}
                  aria-label={t.next} data-testid="concept-next"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  style={{ background: "rgba(8,11,18,0.85)", border: `1px solid ${P.cyan}55`, color: P.cyan }}>
                  <ChevronRight size={18} />
                </button>
                {fs && (
                  <button type="button" onClick={() => setFs(false)} aria-label={t.exitFs} data-testid="concept-exit-fs"
                    className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                    style={{ background: "rgba(8,11,18,0.85)", border: `1px solid ${P.border}`, color: "#fff" }}>
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Controls bar: thumbs (desktop) + action buttons */}
          <div className="mt-3 rounded-lg p-3 flex flex-wrap items-center gap-3" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
            {/* Thumbnail strip — desktop only */}
            <div className="hidden md:flex flex-1 min-w-0 gap-1.5 overflow-x-auto" data-testid="concept-thumbs">
              {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map((n) => (
                <button key={n} type="button" onClick={() => setPage(n)}
                  aria-label={t.thumbAria(n)} data-testid={`concept-thumb-${n}`}
                  className="shrink-0 rounded overflow-hidden transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  style={{
                    width: 56, aspectRatio: "16/9",
                    border: `1.5px solid ${page === n ? P.cyan : P.border}`,
                    opacity: page === n ? 1 : 0.6,
                  }}>
                  <img src={PAGE_URL(n)} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => setFs(true)}
                aria-label={t.fullscreen} data-testid="concept-fs"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[11.5px] font-semibold rounded-md transition hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                style={{ border: `1px solid ${P.border}`, color: P.ink }}>
                <Maximize2 size={12} /> {t.fullscreen}
              </button>
              <a href={PDF_URL} target="_blank" rel="noopener noreferrer"
                data-testid="concept-open-pdf"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[11.5px] font-semibold rounded-md transition hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                style={{ border: `1px solid ${P.border}`, color: P.ink }}>
                <ExternalLink size={12} /> {t.openPdf}
              </a>
              <a href={PDF_URL} download data-testid="concept-download"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[11.5px] font-semibold rounded-md transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                style={{ background: P.cyan, color: "#04121a" }}>
                <Download size={12} /> {t.download}
              </a>
            </div>
          </div>
        </>
      )}

      <div data-testid="concept-disclaimer"
        className="mt-6 text-[11.5px] italic leading-relaxed p-4 rounded-md max-w-4xl"
        style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }}>
        {t.disclaimer}
      </div>
    </div>
  );
}
